import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { MenuService } from 'src/app/components/shared/layout/menu-service/menu.service';

export interface VoiceCommand {
  command: string;
  action: () => void;
  description: string;
}

@Injectable({
  providedIn: 'root'
})
export class VoiceNavigationService {
  private recognition: any;
  private isListening = false;
  private isListeningSubject = new BehaviorSubject<boolean>(false);
  private lastCommandSubject = new BehaviorSubject<string>('');
  private isSupported = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;

  // Available voice commands
  private commands: VoiceCommand[] = [
    {
      command: 'go to dashboard',
      action: () => this.router.navigate(['/dashboard']),
      description: 'Navigate to dashboard'
    },
    {
      command: 'go to signups',
      action: () => this.router.navigate(['/signups']),
      description: 'Navigate to signups'
    },
    {
      command: 'go to credentials',
      action: () => this.router.navigate(['/credentials']),
      description: 'Navigate to credentials'
    },
    {
      command: 'go to configurations',
      action: () => this.router.navigate(['/configurations']),
      description: 'Navigate to configurations'
    },
    {
      command: 'go to members',
      action: () => this.router.navigate(['/members']),
      description: 'Navigate to members'
    },
    {
      command: 'go to funding sources',
      action: () => this.router.navigate(['/funding-sources']),
      description: 'Navigate to funding sources'
    },
    {
      command: 'go to statistics',
      action: () => this.router.navigate(['/statistics']),
      description: 'Navigate to statistics'
    },
    {
      command: 'go to partner pay',
      action: () => this.router.navigate(['/partnerpay']),
      description: 'Navigate to partner pay'
    },
    {
      command: 'go to users',
      action: () => this.router.navigate(['/users']),
      description: 'Navigate to users'
    },
    {
      command: 'go to roles',
      action: () => this.router.navigate(['/roles']),
      description: 'Navigate to roles'
    },
    {
      command: 'go to trips',
      action: () => this.router.navigate(['/trips']),
      description: 'Navigate to trips'
    },
    {
      command: 'go to profile',
      action: () => this.router.navigate(['/profile']),
      description: 'Navigate to profile'
    },
    {
      command: 'stop listening',
      action: () => this.stopListening(),
      description: 'Stop voice recognition'
    },
    {
      command: 'what can I say',
      action: () => this.speakAvailableCommands(),
      description: 'List available voice commands'
    },
    {
      command: 'help',
      action: () => this.speakAvailableCommands(),
      description: 'List available voice commands'
    }
  ];

  constructor(
    private router: Router,
    private menuService: MenuService
  ) {
    this.initializeSpeechRecognition();
    this.generateCommandsFromMenu();
  }

  private initializeSpeechRecognition(): void {
    if (!this.isSupported) {
      console.warn('Speech recognition is not supported in this browser');
      return;
    }

    // Initialize speech recognition
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    this.recognition = new SpeechRecognition();
    
    this.recognition.continuous = false;
    this.recognition.interimResults = false;
    this.recognition.lang = 'en-US';

    this.recognition.onstart = () => {
      this.isListening = true;
      this.isListeningSubject.next(true);
    };

    this.recognition.onend = () => {
      this.isListening = false;
      this.isListeningSubject.next(false);
    };

    this.recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript.toLowerCase().trim();
      this.lastCommandSubject.next(transcript);
      this.processCommand(transcript);
    };

    this.recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      this.isListening = false;
      this.isListeningSubject.next(false);
    };
  }

  private processCommand(transcript: string): void {
    console.log('Processing voice command:', transcript);
    
    // Find matching command
    const matchedCommand = this.commands.find(cmd => 
      transcript.includes(cmd.command.toLowerCase())
    );

    if (matchedCommand) {
      this.speak(`Executing: ${matchedCommand.description}`);
      matchedCommand.action();
    } else {
      this.speak(`Command not recognized. Say "help" for available commands.`);
    }
  }

  startListening(): void {
    if (!this.isSupported) {
      this.speak('Voice navigation is not supported in this browser');
      return;
    }

    if (!this.isListening) {
      try {
        this.recognition.start();
        this.speak('Listening for voice commands');
      } catch (error) {
        console.error('Error starting speech recognition:', error);
      }
    }
  }

  stopListening(): void {
    if (this.isListening && this.recognition) {
      this.recognition.stop();
      this.speak('Voice recognition stopped');
    }
  }

  toggleListening(): void {
    if (this.isListening) {
      this.stopListening();
    } else {
      this.startListening();
    }
  }

  private speakAvailableCommands(): void {
    const commandList = this.commands
      .map(cmd => `${cmd.command}: ${cmd.description}`)
      .join('. ');
    this.speak(`Available commands: ${commandList}`);
  }

  private speak(text: string): void {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.pitch = 1;
      speechSynthesis.speak(utterance);
    }
  }

  getIsListening(): Observable<boolean> {
    return this.isListeningSubject.asObservable();
  }

  getLastCommand(): Observable<string> {
    return this.lastCommandSubject.asObservable();
  }

  isVoiceSupported(): boolean {
    return this.isSupported;
  }

  getAvailableCommands(): VoiceCommand[] {
    return this.commands;
  }

  private generateCommandsFromMenu(): void {
    try {
      // Get menu for admin role (you can adjust this based on user role)
      const menus:any = this.menuService.getMenu('admin');
      
      // Add navigation commands for each menu item
      Object.keys(menus).forEach(menuKey => {
        const menu = menus[menuKey];
        if (menu && menu.url) {
          const command = {
            command: `go to ${menu.title.toLowerCase()}`,
            action: () => this.router.navigate([`/${menu.url}`]),
            description: `Navigate to ${menu.title}`
          };
          
          // Check if command already exists
          const existingCommand = this.commands.find(cmd => cmd.command === command.command);
          if (!existingCommand) {
            this.commands.push(command);
          }
        }
      });
    } catch (error) {
      console.warn('Could not generate commands from menu:', error);
    }
  }
} 